//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { ScheduleInstagramPostErrorCode } from '@apis/publish/publishApiModels';
import { InstagramReelsCreativeNotesType } from '@instagram-account/redux/instagram-reels-post-details/instagramReelsPostDetailsModel';
import {
  MAX_IG_HASHTAG_QUANTITY,
  MAX_IG_MESSAGE_LENGTH,
} from '@instagram-account/redux/post-details/instagramPostDetailsUtils';
import { InstagramStoryGroupClipCustomizationType } from '@models/IScheduledInstagramStoryGroup';
import {
  ScheduledItemFailureStatus,
  deserializeScheduledItemFailureStatus,
} from '@models/ScheduledItemFailureStatus';
import { SocialNetwork, getSocialNetworkName } from '@models/SocialNetwork';
import {
  MAX_TWITTER_MEDIA_HEIGHT_PX,
  MAX_TWITTER_MEDIA_WIDTH_PX,
  MIN_TWITTER_MEDIA_HEIGHT_PX,
  MIN_TWITTER_MEDIA_WIDTH_PX,
} from '@twitter/redux/post-details/twitterPostDetailsModel';
import { isDefined } from '@utils/filterPredicates';
import { pluralize } from '@utils/stringUtils';

export const IGPD_PREVIEW_WIDTH = 420;

export const PostDetailsHeaderText = {
  CreatePost: 'Create Feed Post',
  CreateReelsPost: 'Create Reels Post',
  ReviewDraft: 'Review Draft',
  ReviewPost: 'Review Feed Post',
  ReviewReelsPost: 'Review Reels Post',
  ReviewStory: 'Review Story Post',
  ScheduleStory: 'Schedule to Your Story',
} as const;

export const PostDetailsScheduleButtonText = {
  Schedule: 'Schedule',
  SaveChanges: 'Save Changes',
} as const;

export const PostDetailsAddMediaConfirmationMsg = {
  AspectRatio:
    'Automatic posting is not supported with the selected media dimensions. Adding this media will disable automatic posting, and remove your tagged users, tagged location and first comment.',
} as const;

export const SameAspectRatioInfoCopy = `For carousel posts, all media must have the same aspect ratio. Instagram will
  automatically resize all media to match the first piece of media, or you can switch to manual posting for more control.`;

export const PostDetailsUserSectionTooltip = {
  Auto: `Choose a mobile device to receive a confirmation when we've posted to Instagram.`,
  Manual:
    'Choose a mobile device to receive a notification reminding you to post to Instagram.',
} as const;

export const PostDetailsLocationSectionPlaceholder = {
  Auto: 'Search for a location to tag...',
  Manual: 'Unavailable when posting manually',
} as const;

export const PostDetailsLocationSectionTooltip =
  'In order to tag locations, you must set the post method to automatically post to Instagram.';

export const PostDetailsTaggedUserSectionPlaceholder = {
  Auto: 'Click on the photo to tag public users',
  Manual: 'Unavailable when posting manually',
  Video: 'Unavailable for video posts',
  NoMedia: 'Select an image to tag public users',
} as const;

export const PostDetailsTaggedUserSectionTooltip = {
  Auto: 'Click on the photo to tag public users.',
  Manual:
    'In order to tag users, you must set the post method to automatically post to Instagram.',
  Video: 'Instagram does not support tagging users in videos.',
} as const;

export const PostDetailsTaggedProductSectionPlaceholder = {
  Auto: 'Click on the photo to add Instagram Shop Tags',
  Manual: 'Unavailable when posting manually',
  Video: 'Add Shop Tags',
  NoMedia: 'Select media to add Instagram Shop Tags',
} as const;

export const PostDetailsTaggedProductSectionTooltip = {
  Auto: 'Select a point on the photo to add an Instagram Shop Tag.',
  Manual:
    'In order to enable Shop Tags, you must set the post method to automatically post to Instagram.',
  Video: 'Select the Add Shop Tags link to add an Instagram Shop Tag.',
} as const;

export const PostDetailsUrlSectionTooltip = `Add a link to your Story. Link to a Showroom to make your Story shoppable!`;

export const PostDetailsUrlSectionTooltipUnavailable = `Links are not available when automatically posting.`;

export const getRemainingText = (unitSingularForm: string, remaining: number) =>
  `${pluralize(remaining, unitSingularForm)} remaining`;

export const getTooManyHashtagsErrorText = (hashtagsOverLimit: number) =>
  `Up to ${MAX_IG_HASHTAG_QUANTITY} hashtags are allowed in the caption of a post. Please remove at least ${pluralize(
    hashtagsOverLimit,
    'hashtag',
  )} to continue.`;

export const getCaptionTooLongErrorText = (charOverLimit: number) =>
  `A maximum of ${MAX_IG_MESSAGE_LENGTH} characters are allowed in the caption of a post. Please remove at least ${pluralize(
    charOverLimit,
    'character',
  )} to continue.`;

export const getFirstCommentTooManyHashtagsErrorText = (
  hashtagsOverLimit: number,
) =>
  `Up to ${MAX_IG_HASHTAG_QUANTITY} hashtags are allowed in a post comment. Please remove at least ${pluralize(
    hashtagsOverLimit,
    'hashtag',
  )} to continue.`;

export const getFirstCommentTooLongErrorText = (charOverLimit: number) =>
  `A maximum of ${MAX_IG_MESSAGE_LENGTH} characters are allowed in a post comment. Please remove at least ${pluralize(
    charOverLimit,
    'character',
  )} to continue.`;

export const PostDetailsFormFieldErrorText = {
  InvalidLabel:
    'Labels can only contain letters, numbers, dashes (-), and underscores (_).',
  InvalidMediaAspectRatio:
    "The media you selected cannot be scheduled because it does not meet Instagram's aspect ratio requirements (4:5 to 1.91:1 for images and 9:16 to 1.91:1 for videos). Resize the media or switch to manual posting.",
  InvalidUrl:
    'Please input a valid URL (http(s)://www.example.com), or save as a draft.',
  InvalidUser:
    'One or more of the users you have tagged is private or does not exist.',
  InvalidVideoLength:
    "The video you selected cannot be scheduled because it does not meet Instagram's video length requirements (3 to 60 seconds). Trim the video or switch to manual posting.",
  InvalidReelsVideoSize: "Reels can't be larger than 1GB.",
  InvalidFeedVideoSize: "Feed videos can't be larger than 100MB.",
  InvalidIgImageSize: "Images can't be larger than 8MB.",
  NoMedia: 'Please add media, or save as a draft.',
  NoMediaStoryClip: 'Please add media to all clips, or save as a draft.',
  MediaIsGif: 'GIF posts are not currently supported on Instagram',
  NoNotificationUser:
    'Please choose who should receive notifications for this post, or save as a draft.',
  NoPostDate: 'Please select a date, or save as a draft.',
  NoPostTime: 'Please select a time, or save as a draft.',
  PostTimeInPast:
    'Please select a post time in the future, or save as a draft.',
  InvalidStoryVideoMessage:
    'Videos must be MOV or MP4, no more than 100MB, and 3-60 seconds.',
} as const;

export enum PostDetailsUserTagErrorMessage {
  InvalidCharacters = 'Usernames can contain a maximum of only 30 letters, numbers, periods, and underscores and cannot start or end with a period.',
  DuplicateUsername = 'You have already tagged this user in this image.',
}

export enum PostDetailsPublishingStrategyMessage {
  Automatic = "We'll publish this post on your behalf.",
  Manual = "We'll send a mobile notification when it's time to manually post.",

  // Disabled messages
  ImageAspectRatio = 'Automatic posting is not supported with the current image dimensions.',
  VideoAspectRatio = "Automatic posting is not supported with the current video dimensions. We'll send you a reminder when it's time to manually post.",
  VideoLength = "The video you selected cannot be posted automatically because it does not meet Instagram's video duration requirements (3-60 seconds). We'll send you a reminder when it's time to post manually.",
  CarouselWithoutAutoPosting = "Gallery posts cannot be posted automatically. We'll send you a reminder on your mobile device when it's time to manually post.",
  ReelsAspectRatio = "Reels automatic posting is not supported with the current video dimensions. We'll send you a reminder when it's time to manually post.",
  ReelsVideoLength = "The video you selected cannot be posted automatically because it does not meet Instagram's Reels duration requirements (3 seconds-15 minutes). We'll send you a reminder when it's time to post manually.",
  Story = "Story posts cannot be posted automatically. We'll send you a reminder on your mobile device when it's time to manually post.",
  VideoIssue = "Due to an Instagram issue, video posts currently cannot be posted automatically. We'll send you a reminder on your mobile device when it's time to manually post.",
  InstagramBusiness = "Automatic posting is not supported without an Instagram business account with the Automatic Publishing permission. Please verify your connections in Account Settings. We'll send you a reminder when it's time to manually post.",
}

export const PostDetailsPublishingStrategyTooltip = `Automatic posting will publish this post on your behalf. \n\n Turn this off if you would prefer to receive a mobile notification at the scheduled time reminding you to post.`;

export const InstagramPostDefaultsModalTooltip = {
  DefaultNotificationUser:
    'Choose a user to receive notifications for your posts by default.',
  DefaultPostTime: 'Choose a default time of day for your scheduled posts.',
  DefaultPublishingStrategy:
    'Choose whether posts should be published on your behalf by default.',
} as const;

export const InstagramRemoveAutomaticFieldsModal = {
  PostAutomatic:
    'Disabling automatic posting will remove your tagged users, shop tags, tagged location, and first comment. Do you want to continue?',
  ReelsAutomatic:
    'Disabling automatic posting will remove your first comment, cover image, and shop tags. Do you want to continue?',
  ReelsManual:
    'Enabling automatic posting will remove your tagged users, location, and creative notes. Do you want to continue?',
  StoryManual: "This will remove any links and stickers you've added.",
};

export const InstagramRemoveAutomaticTitleModal = {
  ToManual: 'Switch From Automatic to Manual',
  ToAutomatic: 'Switch From Manual to Automatic',
};

export const VideoThumbnailButtonTooltip =
  'Switch to Automatic posting to edit your cover image.';

export const PostDetailsCrossPostingTooltip = {
  CrossPostingInfo: `This will create a Facebook and/or X post with the same media, caption, date, and time. You'll be able to edit any of these later on.`,
  IncompatibleMedia:
    'Instagram posts that contain both images and videos cannot be scheduled to Facebook or X.',
  FbSchedulingDisabled:
    'To gain access to Facebook scheduling, reach out to your Account Manager or Bazaarvoice Support.',
  FbConnectionIncomplete: `You won't be able to post this to Facebook until your Facebook account is properly connected.`,
  TwConnectionIncomplete: `You won't be able to post this to X until your X account is properly connected.`,
  TwCaptionLength:
    'Your Instagram caption is over the 280 character limit for a post. Reduce the character count to also schedule this post to X.',
  TwImageLimit:
    'X posts can only contain up to 4 images per post. Remove some media to also schedule this post to X.',
  TwMediaTooLarge: `Media scheduled to X must be smaller than ${MAX_TWITTER_MEDIA_WIDTH_PX}x${MAX_TWITTER_MEDIA_HEIGHT_PX}px. Select a smaller image if you'd like to schedule that content to X.`,
  TwMediaTooSmall: `Media scheduled to X must be larger than ${MIN_TWITTER_MEDIA_WIDTH_PX}x${MIN_TWITTER_MEDIA_HEIGHT_PX}px. Select a larger image if you'd like to schedule that content to X.`,
  TwVideoLengthLimit: `Videos scheduled to X can only be 140 seconds long. Select a shorter video if you'd like to schedule that content to X.`,
} as const;

export const SwapScheduledToDraftCrossPostMsg =
  'Saving this post as a draft will unschedule it. Posts scheduled to other platforms with the same content will not be unscheduled. Continue to save as draft?';
export const SwapScheduledToDraftMsg =
  'Saving this post as a draft will remove it from your scheduled Instagram posts. Are you sure you want to save this as a draft?';

export const getGenericPostTimeErrorMessage = (
  network: SocialNetwork | undefined,
) =>
  `Something went wrong when publishing this post${isDefined(network) ? ` to ${getSocialNetworkName(network)}` : ''}. Please try again or contact Bazaarvoice Support.`;

export const getPostDetailsFailureStatusMessage = (
  itemFailureStatus: ScheduledItemFailureStatus,
): string => {
  switch (itemFailureStatus) {
    case ScheduledItemFailureStatus.InstagramPublishingExpiredToken:
      return 'The token used to publish this post has expired. Please go to Account Settings to reconnect your Facebook account, then update the scheduled time and try again.';
    case ScheduledItemFailureStatus.InstagramPublishingFailure:
      return 'Something went wrong with Instagram. Please try again or contact Bazaarvoice Support.';
    case ScheduledItemFailureStatus.InstagramPublishingUnexpectedRetriableException:
      return 'We are unable to verify if this post was published to Instagram. If it was not published, please try again or contact Bazaarvoice Support.';
    case ScheduledItemFailureStatus.InstagramPublishingImageSizeTooLarge:
      return 'This image is too large to be automatically posted. Please reduce the file size to less than 8 MB and try again.';
    case ScheduledItemFailureStatus.InstagramPublishingInvalidTaggedUser:
      return 'One or more tagged users in this post is invalid. Please ensure all usernames are spelled correctly, and no tagged users have private accounts.';
    case ScheduledItemFailureStatus.InstagramPublishingMediaNotReadyException:
      return '';
    case ScheduledItemFailureStatus.InstagramPublishingOAuthException:
      return 'Something went wrong with Instagram. Please try again or contact Bazaarvoice Support.';
    case ScheduledItemFailureStatus.InstagramPublishingPermissionDeniedException:
      return 'We didn’t have the required permissions to publish this post. Check your Instagram account connection and try again.';
    case ScheduledItemFailureStatus.InstagramPublishingThrottlingException:
      return 'We were unable to post to Instagram at the scheduled time. Please try again.';
    case ScheduledItemFailureStatus.InstagramPublishingUnsupportedAspectRatio:
      return 'The aspect ratio of this image must be between 4:5 and 1.91:1. Please try again with a resized image.';
    case ScheduledItemFailureStatus.InstagramPublishingUploadException:
      return `Due to an Instagram issue your post failed to publish at the scheduled time. Instagram's support team is actively investigating. In the meantime please try scheduling again or switch to manual scheduling.`;
    case ScheduledItemFailureStatus.InstagramPublishingFeatureBlockException:
      return `Your post has failed to schedule due to Facebook reporting that you have performed too many actions in a short time period. You'll be blocked from automatically posting for 12-24 hours. Please switch to scheduling manual posts to continue.`;
    case ScheduledItemFailureStatus.InstagramPublishingVideoFailureException:
      return `Your video cannot be published automatically. Please switch to scheduling manual posts to continue.`;
    case ScheduledItemFailureStatus.InstagramPublishingDailyLimitException:
      return `Your post failed to publish because you have reached Facebook's daily posting limit of 25 posts in 24 hours. Please switch to manual scheduling for this post to publish it.`;
    default:
      return getGenericPostTimeErrorMessage(SocialNetwork.Instagram);
  }
};

export const PostDetailsSchedulingErrorMessage: Record<
  ScheduleInstagramPostErrorCode | 'Other',
  string
> = {
  // 200 Range: Things wrong with the post
  [ScheduleInstagramPostErrorCode.INSTAGRAM_PUBLISHING_UNSUPPORTED_ASPECT_RATIO]:
    'The aspect ratio of this image must be between 4:5 and 1.91:1. Please try again with a resized image.',
  [ScheduleInstagramPostErrorCode.INSTAGRAM_PUBLISHING_IMAGE_SIZE_TOO_LARGE]:
    'This image is too large to be automatically posted. Please reduce the file size to less than 8 MB and try again.',
  [ScheduleInstagramPostErrorCode.INSTAGRAM_PUBLISHING_INVALID_TAGGED_USER]:
    'Your post contains one or more users that are private or do not exist. Please check your tagged users and try again.',

  // 400 Range: Unrecoverable errors
  [ScheduleInstagramPostErrorCode.INSTAGRAM_PUBLISHING_UNEXPECTED_ERROR]:
    'Something went wrong. Please try again or contact Bazaarvoice Support.',
  [ScheduleInstagramPostErrorCode.INSTAGRAM_PUBLISHING_PERMISSION_DENIED_EXCEPTION]:
    'Something went wrong with your Facebook permissions. Please try reconnecting your Facebook account in the Settings page. If the issue persists, please contact Bazaarvoice Support.',
  [ScheduleInstagramPostErrorCode.INSTAGRAM_PUBLISHING_INVALID_PARAMETER]:
    'Something went wrong. Please check post settings and try again. If the issue persists, please contact Bazaarvoice Support to resolve this error.',

  // 500 Range: Unexpected service related issues
  [ScheduleInstagramPostErrorCode.INSTAGRAM_PUBLISHING_UNEXPECTED_RETRIABLE_ERROR]:
    'We were unable to schedule your image at this time due to a temporary issue from Instagram. Please try again.',
  [ScheduleInstagramPostErrorCode.INSTAGRAM_PUBLISHING_EXPIRED_TOKEN]:
    'The token being used to publish this post has expired. Please go to Account Settings to reconnect your Facebook account, then try again.',
  [ScheduleInstagramPostErrorCode.INSTAGRAM_PUBLISHING_THROTTLING_EXCEPTION]:
    'We were unable to schedule this post. Please try again.',
  [ScheduleInstagramPostErrorCode.INSTAGRAM_PUBLISHING_OAUTH_EXCEPTION]:
    'Something went wrong. Please try again or contact Bazaarvoice Support.',
  [ScheduleInstagramPostErrorCode.INSTAGRAM_PUBLISHING_UPLOAD_EXCEPTION]:
    'Your Instagram post failed to schedule. Instagram was unable to process your image at this time. Please try again later or switch to manual scheduling.',
  [ScheduleInstagramPostErrorCode.INSTAGRAM_PUBLISHING_MEDIA_NOT_READY_EXCEPTION]:
    'Your Instagram post failed to schedule. Instagram was unable to process your video at this time. Please try again later or switch to manual scheduling.',
  [ScheduleInstagramPostErrorCode.INSTAGRAM_PUBLISHING_DOWNLOAD_TIMEOUT]:
    'Instagram is experiencing issues and failed to download your image. Please try to schedule your post again or switch to manual posting.',
  [ScheduleInstagramPostErrorCode.INSTAGRAM_PUBLISHING_FEATURE_BLOCKED]:
    "Your post has failed to schedule due to Facebook reporting that you have performed too many actions in a short time period. You'll be blocked from automatically posting for 12-24 hours. Please switch to scheduling manual posts to continue",
  [ScheduleInstagramPostErrorCode.INSTAGRAM_PUBLISHING_INTERMITTENT_VIDEO_FAILURE]:
    'Your Instagram Video has failed to schedule due to Facebook API issues. Please try again or switch to manual scheduling.',
  [ScheduleInstagramPostErrorCode.INSTAGRAM_PUBLISHING_MEDIA_CONTAINER_CORRUPTED]:
    'Something went wrong. Please try again or contact Bazaarvoice Support.',

  Other:
    'Something went wrong. Please try again or contact Bazaarvoice Support.',
};

export const PostDetailsFinalValidationErrorMessage =
  'Please address the errors above and try again.';

export const SwapDraftToScheduledErrorMessage =
  'We scheduled your post, but a server issue prevented us from removing it from your drafts. Please delete this draft manually, or contact support for help.';
export const SwapScheduledToDraftErrorMessage =
  'We have saved a copy of this post to your drafts, but a server issue prevented us from removing it from your schedule. Please delete this post from your schedule manually, or contact support for help.';

const postDetailsPrefix = 'ig-post-details-';

export const PostDetailsTrackingIds = {
  ADD_MEDIA_EMPTY_STATE_BUTTON: `${postDetailsPrefix}add-media-empty-state-button`,
  ADD_ADDITIONAL_MEDIA_BUTTON: `${postDetailsPrefix}add-additional-media-button`,
  L2B_CTA_BUTTON: `${postDetailsPrefix}l2b-cta-button`,
  MEDIA_EDIT_ACTION_BUTTON: `${postDetailsPrefix}media-edit-action-button`,
  MEDIA_REMOVE_ACTION_BUTTON: `${postDetailsPrefix}media-remove-action-button`,
  MEDIA_VIEW_DETAILS_ACTION_BUTTON: `${postDetailsPrefix}media-view-details-action-button`,
  MEDIA_BULK_ACTION_SELECT: `${postDetailsPrefix}media-bulk-action-select`,

  VIDEO_PREVIEW_PLAY_PAUSE_BUTTON: `${postDetailsPrefix}video-preview-play-pause-button`,
  EDIT_VIDEO_COVER_BUTTON: `${postDetailsPrefix}edit-video-cover-button`,
  USER_TAGGING_OVERLAY: `${postDetailsPrefix}user-tagging-overlay`,
  GALLERY_PREVIEW_LEFT_BUTTON: `${postDetailsPrefix}gallery-preview-left-button`,
  GALLERY_PREVIEW_RIGHT_BUTTON: `${postDetailsPrefix}gallery-preview-right-button`,

  CAPTION: `${postDetailsPrefix}caption`,
  SMART_INPUT_CAPTION: `${postDetailsPrefix}smart-input-caption`,
  EMOJI_PICKER: `${postDetailsPrefix}emoji-picker`,
  ADD_FIRST_COMMENT: `${postDetailsPrefix}add-first-comment`,
  REMOVE_FIRST_COMMENT: `${postDetailsPrefix}remove-first-comment`,
  FIRST_COMMENT: `${postDetailsPrefix}first-comment`,
  SMART_INPUT_FIRST_COMMENT: `${postDetailsPrefix}smart-input-first-comment`,
  FIRST_COMMENT_EMOJI_PICKER: `${postDetailsPrefix}first-comment-emoji-picker`,
  LOCATION_TAGGING_SECTION: `${postDetailsPrefix}location-tagging-section`,
  PRODUCT_TAGGING_SECTION: `${postDetailsPrefix}product-tagging-section`,
  DATE_PICKER: `${postDetailsPrefix}date-picker`,
  DATE_PICKER_TODAY_BUTTON: `${postDetailsPrefix}date-picker-today-button`,
  DATE_PICKER_BEST_TIMES_AND_DATE_CTA: `${postDetailsPrefix}date-picker-best-times-and-dates-cta`,
  DATE_PICKER_BEST_TIMES_CTA: `${postDetailsPrefix}date-picker-best-times-cta`,
  TIME_PICKER: `${postDetailsPrefix}time-picker`,
  TIME_PICKER_NOW_BUTTON: `${postDetailsPrefix}time-picker-now-button`,
  NOTIFICATION_USER: `${postDetailsPrefix}notification-user`,
  PUBLISHING_STRATEGY_CHECKBOX: `${postDetailsPrefix}publishing-strategy-checkbox`,
  PUBLISHING_STRATEGY_RESIZE_IMAGE_BUTTON: `${postDetailsPrefix}publishing-strategy-resize-image-button`,
  CROSS_POST_CHECKBOX: `${postDetailsPrefix}cross-post-checkbox`,
  CROSS_POST_FB_CHECKBOX: `${postDetailsPrefix}fb-cross-post-checkbox`,
  CROSS_POST_TW_CHECKBOX: `${postDetailsPrefix}tw-cross-post-checkbox`,
  ADD_SHOP_TAG: `${postDetailsPrefix}add-shop-tag`,
  SHOP_TAGS: `${postDetailsPrefix}shop-tags`,

  CONFIGURE_POST_DEFAULTS_BUTTON: `${postDetailsPrefix}configure-post-defaults-button`,
  SET_POST_DEFAULTS_BUTTON: `${postDetailsPrefix}set-post-defaults-button`,

  CONFIRM_CHANGE_AUTO_TO_MANUAL_BUTTON: `${postDetailsPrefix}confirm-change-auto-to-manual-button`,
  CANCEL_CHANGE_AUTO_TO_MANUAL_BUTTON: `${postDetailsPrefix}cancel-change-auto-to-manual-button`,
  CONFIRM_ADD_MEDIA_BUTTON: `${postDetailsPrefix}confirm-add-media-button`,
  CANCEL_ADD_MEDIA_BUTTON: `${postDetailsPrefix}cancel-add-media-button`,

  CANCEL_BUTTON: `${postDetailsPrefix}cancel-button`,
  CONFIRM_DISCARD_CHANGES_BUTTON: `${postDetailsPrefix}confirm-discard-changes-button`,
  CANCEL_DISCARD_CHANGES_BUTTON: `${postDetailsPrefix}cancel-discard-changes-button`,
  SAVE_AS_DRAFT_BUTTON: `${postDetailsPrefix}save-as-draft-button`,
  CONFIRM_SAVE_SCHEDULED_POST_AS_DRAFT_BUTTON: `${postDetailsPrefix}confirm-save-scheduled-post-as-draft-button`,
  CANCEL_SAVE_SCHEDULED_POST_AS_DRAFT_BUTTON: `${postDetailsPrefix}cancel-save-scheduled-post-as-draft-button`,
  SCHEDULE_BUTTON: `${postDetailsPrefix}schedule-button`,

  STORY_GROUP_EMPTY_CLIP: `${postDetailsPrefix}story-group-empty-clip`,
  STORY_GROUP_ADD_CLIP_BUTTON: `${postDetailsPrefix}story-group-add-clip-button`,
  STORY_GROUP_REMOVE_CLIP_BUTTON: `${postDetailsPrefix}story-group-remove-clip-button`,
  STORY_GROUP_SWIPE_UP_LINK: `${postDetailsPrefix}story-group-swipe-up`,
  STORY_GROUP_ADD_STICKER_BUTTON: `${postDetailsPrefix}story-group-add-sticker-button`,
  STORY_GROUP_ADD_SHOWROOM_BUTTON: `${postDetailsPrefix}story-group-add-showroom-button`,

  REELS_ADD_MEDIA_EMPTY_STATE_BUTTON: `${postDetailsPrefix}reels-add-media-empty-state-button`,
  REELS_ADD_CREATIVE_NOTES_BUTTON: `${postDetailsPrefix}reels-add-creative-notes-button`,
  REELS_ADD_CREATIVE_NOTES_ACTION_SHEET_BUTTON: `${postDetailsPrefix}reels-add-creative-notes-action-sheet-button`,
  REELS_REMOVE_CREATIVE_NOTES_BUTTON: `${postDetailsPrefix}reels-remove-creative-notes-button`,
  REELS_TAG_USERS: `${postDetailsPrefix}reels-tag-users`,
  REELS_TAG_LOCATION: `${postDetailsPrefix}reels-tag-location`,
  REELS_SHARE_TO_FEED: `${postDetailsPrefix}reels-share-to-feed`,
  REELS_EDIT_COVER_IMAGE_BUTTON: `${postDetailsPrefix}reels-edit-cover-image-button`,
} as const;

export const InstagramStoryGroupClipCustomizationTypeLabel: Record<
  InstagramStoryGroupClipCustomizationType,
  string
> = {
  [InstagramStoryGroupClipCustomizationType.SwipeUpUrl]: 'Link',
  [InstagramStoryGroupClipCustomizationType.TextOverlay]: 'Text Overlay',
  [InstagramStoryGroupClipCustomizationType.Location]: 'Location',
  [InstagramStoryGroupClipCustomizationType.AtMention]: '@ Mention',
  [InstagramStoryGroupClipCustomizationType.Hashtag]: 'Hashtag',
  [InstagramStoryGroupClipCustomizationType.Poll]: 'Poll',
  [InstagramStoryGroupClipCustomizationType.Question]: 'Question',
  [InstagramStoryGroupClipCustomizationType.Gif]: 'GIF',
  [InstagramStoryGroupClipCustomizationType.Other]: 'Other',
};

export const STORY_GROUP_PREVIEW_STICKER_HELP_TEXT =
  'Stickers will be available in the Bazaarvoice Vibe mobile app to copy and paste into your Instagram story.';
export const STORY_GROUP_PREVIEW_STICKER_HELP_TEXT_UNAVAILABLE =
  'Stickers are unavailable when automatically posting. To add stickers in the Bazaarvoice Vibe mobile app, switch to manual posting.';
export const ADD_STICKER_TOOLTIP_TEXT = `Notes help you keep track of any text overlays, hashtags, mentions, locations tags, or more that you'll want to add to your story. We'll make it easy to copy and paste your notes when it's time to post.`;

export const ADD_STICKER_TOOLTIP_UNAVAILABLE_TEXT = `Notes are not available when automatically posting.`;

export const InstagramReelsCreativeNotesTypeLabel: Record<
  InstagramReelsCreativeNotesType,
  string
> = {
  audio: 'Audio',
  effects: 'Effects',
  other: 'Other',
  stickers: 'Stickers',
  textOverlay: 'Text Overlay',
} as const;
export const OrderedInstagramReelsCreativeNotesTypes: Readonly<
  InstagramReelsCreativeNotesType[]
> = ['textOverlay', 'audio', 'effects', 'stickers', 'other'] as const;
// TODO: PD-193955 - Add/update copy
export const REELS_CREATIVE_NOTES_SECTION_TOOLTIP_TEXT = {
  Manual: `Notes help you keep track of any text overlays, audio, effects, stickers, locations tags, or other details that you'll want to add to your Reels post. We'll make it easy to copy and paste your notes when it's time to post.`,
  Auto: `Some customization options, like audio naming, are not available when automatically posting.`,
};
export const REELS_SHARE_TO_FEED_SECTION_TOOLTIP_TEXT = {
  Manual: `This will help you remember whether to share your Reel to your feed when it comes time to post.`,
  Auto: `Bazaarvoice will automatically post this Reel to your Instagram Reels and feed.`,
};
export const REELS_TAGGED_USERS_SECTION_TOOLTIP_TEXT = {
  Manual: `This will help you remember who to tag when it comes time to post your Reel.`,
  Auto: `You cannot tag users in automatic Reels posts.`,
};

// Instagram Reels limits to be eligible to be published to Reels tab
export const REELS_TAB_MIN_VIDEO_DURATION_MILLIS = 5000; // 5 secs in ms
export const REELS_TAB_MAX_VIDEO_DURATION_MILLIS = 90000; // 90 secs in ms
export const REELS_TAB_IDEAL_ASPECT_RATIO = 9 / 16;

// Hard limits for Instagram Reels media set by Instagram API
export const REELS_MIN_VIDEO_DURATION_MILLIS = 3000; // 3 secs in ms
export const REELS_MAX_VIDEO_DURATION_MILLIS = 900000; // 15 minutes in ms
export const REELS_MIN_ASPECT_RATIO = 0.01 / 1;
export const REELS_MAX_ASPECT_RATIO = 10 / 1;
export const REELS_MAX_VIDEO_SIZE = 1 * 1024 * 1024 * 1024; // 1gb in bytes
export const STORY_MIN_VIDEO_DURATION_MILLIS = 3000; // 3 secs in ms
export const STORY_MAX_VIDEO_DURATION_MILLIS = 60000; // 1 minute in ms
export const STORY_MAX_VIDEO_SIZE = 100 * 1024 * 1024; // 100mb in bytes
export const STORY_MAX_IMAGE_SIZE = 8 * 1024 * 1024; // 8mb in bytes
export const RECOMMENDED_IG_STORY_ASPECT_RATIO = 9 / 16;
export const RECOMMENDED_IG_STORY_ASPECT_RATIO_BANNER =
  'We recommend selecting media with a 9:16 aspect ratio. Instagram may crop media outside of this aspect ratio.';

export type TagType = 'User' | 'Product';
